/**
 * The video surface's geometry contracts — how a picture sits inside its box,
 * and how a zoomed tile reports what it is showing. These describe pixels on
 * screen, and the overlays, the zoom hook and the motion editor all have to
 * agree on them. Not wire shapes — except `ZoomView`, which a video wall's
 * viewers send each other, so it is a zod schema.
 */

import { z } from 'zod';

import type { VideoMessage } from './webrtc';

/**
 * Which of a camera's two streams a tile is showing. Distinct from
 * `ImageQuality` in ./devices, which grades a still frame the API renders —
 * this one selects an m-line on the live session.
 */
export enum StreamQuality {
    Low = 'low',
    High = 'high',
}

/**
 * What the operator picks for a stream: either of them, or letting the tile's
 * rendered width decide.
 */
export type QualityChoice = StreamQuality | 'auto';

/**
 * How far along a session's setup is. An enum rather than a frozen object: the
 * client computes every one of these, and none is ever parsed from a message.
 */
export enum ConnectionPhase {
    Idle = 'idle',
    Connecting = 'connecting',
    Connected = 'connected',
    Retrying = 'retrying',
}

/** What a camera's picture says in place of its live video. */
export enum LivePlaceholder {
    Connecting = 'connecting',
    Offline = 'offline',
    NoConnection = 'noConnection',
    CodecUnsupported = 'codecUnsupported',
}

/** How the picture fills its element — the CSS object-fit values in use. */
export type ObjectFit = 'contain' | 'cover';

/**
 * One camera of the player, with the server whose session carries it: a player may span servers.
 */
export interface PlayerDevice {
    deviceId: string;
    serverId: string;
    quality?: StreamQuality;
}

/**
 * Where the picture actually lands inside its element once object-fit has
 * letterboxed it: the drawn size and the offset of its top-left corner, in the
 * element's CSS pixels. `getVideoDisplayRect` computes it.
 */
export interface VideoDisplayRect {
    displayW: number;
    displayH: number;
    offsetX: number;
    offsetY: number;
}

/**
 * A digital zoom/pan, as the pure draw helpers still ask for it. Overlays that
 * sit inside the zoom wrapper pass NO_ZOOM, because the CSS transform above
 * them has already done the work.
 */
export interface ZoomTransform {
    zoom: number;
    panX: number;
    panY: number;
    centerX: number;
    centerY: number;
}

/**
 * What the overlay reports at draw time for the diagnostics HUD. It is written
 * by OverlayCanvas and read by DiagnosticsHud, so neither owns it.
 */
export interface OverlayStats {
    newestMetaUtcMs?: number | null;
    /** Displayed-frame UTC minus metadata UTC at the last packet's arrival. */
    metaDeltaMs?: number | null;
    /** Wall clock of that arrival, so a quiet feed greys the readout out. */
    metaArrivalWall?: number;
    objectsInBuffer?: number;
    boxesDrawn?: number;
}

/**
 * A rectangle in normalized picture coordinates, 0..1 from the top-left of the
 * frame — a motion select-area or a crop. Not `Rect` from ./events, which is
 * the wire's pixel box.
 */
export interface NormalizedRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * The same rectangle once it is on screen: CSS pixels of the video element,
 * after the letterbox offset and the digital zoom/pan.
 */
export interface ScreenRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** A crossing line's two endpoints on screen, in the same CSS pixels. */
export interface LineScreenPoints {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** What the pointer is over: a line, and which part of it a drag would move. */
export interface LineHit {
    lineId: string;
    part: 'p1' | 'p2' | 'body';
}

/** One point on screen, in the same CSS pixels. */
export interface ScreenPoint {
    x: number;
    y: number;
}

/**
 * What the pointer is over on a zone. `index` names the corner for a vertex,
 * and for an edge the corner it follows - the midpoint a click adds one at.
 */
export type AreaHit =
    | { areaId: string; part: 'vertex' | 'edge'; index: number } |
    { areaId: string; part: 'body' };

/**
 * One rendered surface of the player. An item is not a device: the same camera
 * may be on screen more than once, and each copy reports its own frames.
 */
export interface PlayerItem {
    itemId: string;
    deviceId: string;
}

/**
 * A tile's digital zoom and pan, as a video wall mirrors it between peers.
 * Pan is in pixels, so it is approximate across differently sized tiles; the
 * zoom factor is exact.
 */
export const ZoomViewSchema = z.object({
    zoom: z.number(),
    panX: z.number(),
    panY: z.number(),
});
export type ZoomView = z.infer<typeof ZoomViewSchema>;

/** The picture's placement in its box, as `useTileZoom.getView` reports it. */
export interface TileView {
    panX: number;
    panY: number;
    shownW: number;
    shownH: number;
    boxW: number;
    boxH: number;
}

/**
 * One RTP↔UTC anchor as the server pushes it over the data channel. Kept per
 * device, newest last; RTP drift over that horizon is negligible.
 */
export interface SyncPoint {
    /** 32-bit RTP timestamp, 90 kHz ticks. */
    rtpTs: number;
    utcUs: number;
    /** The same mapping for the camera's audio, its RTP timestamp in the audio codec's clock. */
    audio?: { rtpTs: number; utcUs: number };
}

/**
 * The headline verdict. An enum rather than a frozen object: the value is
 * computed here and read by the HUD, never parsed out of JSON.
 */
export enum HudStatus {
    Ok = 'ok',
    Warn = 'warn',
    Bad = 'bad',
    Off = 'off',
}

/** What the HUD renders: the verdict, the drift, and the formatted lines. */
export interface HudModel {
    status: HudStatus;
    /** Null when the lag is unknowable. */
    deltaMs: number | null;
    lines: string[];
}

/** What the owner is told about the session's setup. */
export interface PlayerState {
    phase: ConnectionPhase;
    shouldShowError: boolean;
    /**
     * The cameras of this session whose codec the browser rejected, each mapped
     * to the codec the server reported. A rejection is one camera's, so only
     * that camera's tile says so - every other tile of the same server keeps
     * reporting whatever it is actually doing.
     */
    codecUnsupportedByDevice?: Record<string, string>;
    /** The cameras of this session that showed no picture on fresh sessions either. */
    noPictureDevices?: string[];
}
/**
 * What the archive has at a seek's target, as the timeline classifies it
 * against the subject's segment list. 'gap' is a hole - with `nextStart` the
 * playhead can reach, or without one the archive's end; 'writing' is a recent
 * moment the list does not cover yet, most likely a file the server is still
 * writing, which the scene re-issues the seek for once the list catches up.
 */
/** A presented frame's absolute time and whether it is past the pending gate. */
export interface FrameResolution {
    utcMs: number;
    isPastGate: boolean;
}

/**
 * What the archive has at the scene's shared position, for the one camera:
 * 'empty' is no recording there, a hole before its next chunk or past its end, and
 * 'writing' is the live edge still being recorded. 'outside' is case mode with
 * the position outside everything this camera bookmarked.
 */
export type TileArchiveState = 'empty' | 'writing' | 'outside';

/**
 * A seek's freeze signal for the tiles. Every discrete or scrubbed seek bumps
 * the key; `shouldFreeze` engages the snapshot overlay, which only a precise seek
 * asks for - a scrub would flicker through one per frame.
 */
export interface SeekReveal {
    key: number;
    shouldFreeze: boolean;
}

/** A step-back freeze signal for the tiles. */
export interface StepReveal {
    key: number;
}

/**
 * A camera's settled UTC (ms) for a step, tagged with the step it belongs to: a
 * tile reveals only on a target whose key matches the freeze it is holding,
 * never on the prior step's.
 */
export interface StepTarget {
    ms: number;
    key: number;
}

/** One device's seek/anchor state for the diagnostics HUD. */
export interface PlayerDiagnostics {
    phase: ConnectionPhase;
    isDcOpen: boolean;
    hasTrack: boolean;
    anchorUtcMs: number | null;
    anchorRtp: number | null;
    seekTargetMs: number | null;
    seekAckMs: number | null;
    seekSeq: number | null;
    seekRtpBase: number | null;
    hasRevealGate: boolean;
    hasArchiveEnded: boolean;
}
/** What the browser measures for a device's playing stream. */
export interface DeviceStreamInfo {
    codec: string | null;
    width: number;
    height: number;
    fps: number | null;
    packets: number | null;
    framesDecoded: number | null;
}

/** The UTC time of a camera's audio playing at atEpochMs (performance.timeOrigin plus now). */
export interface EpochAudioClock {
    utcUs: number;
    atEpochMs: number;
}

/** The UTC time of a camera's audio playing at atMs, on the clock of the thread that holds it. */
export interface AudioClock {
    utcUs: number;
    atMs: number;
}

/**
 * The decoded picture of a camera whose video comes over the data channel: a MediaStream the
 * tile shows in place of the camera's RTP one. See createDataChannelVideoTrack.
 */
export interface DataChannelVideoTrack {
    readonly stream: MediaStream;
    /** Decodes a frame; after a lost frame, a delta frame waits for the next key frame. */
    push(frame: VideoMessage): void;
    /** What the HUD shows of the stream; null until the first frame decodes. */
    getInfo(): DeviceStreamInfo | null;
    /**
     * The UTC time of the camera's audio playing at atEpochMs (performance.timeOrigin plus
     * performance.now()), which paces the frames; null when there is none.
     */
    setAudioClock(clock: EpochAudioClock | null): void;
    close(): void;
}

/** What the page sends the worker track (see dataChannelVideo.worker.ts). */
export type DataChannelVideoWorkerRequest =
    { type: 'sink'; writable: WritableStream<VideoFrame> } |
    { type: 'frame'; frame: VideoMessage } |
    { type: 'audioClock'; clock: EpochAudioClock | null } |
    { type: 'close' };

/** What the worker track sends the page back. */
export type DataChannelVideoWorkerReply =
    { type: 'track'; track: MediaStreamTrack } |
    { type: 'info'; info: DeviceStreamInfo | null } |
    { type: 'unsupported' };

/**
 * A time range the operator marked on the timeline, to export, bookmark or
 * file into a case. The timeline draws it, the banner reads it and playback
 * loops inside it, so all three have to agree on the shape.
 */
export interface TimelineSelection {
    startMs: number;
    endMs: number;
}

/**
 * One recorded chunk, as the timeline's segment list carries it. A negative
 * duration is a recording still open.
 */
export interface RecordedSegment {
    startTimeMs: number;
    durationMs: number;
}

/** A camera whose recordings are read, with the server whose relay tunnel they come through. */
export interface ArchiveCamera {
    serverId: string;
    cameraId: string;
}

/**
 * One server's slice of a talk-back session: which of its cameras get the same captured
 * audio. useTalkBack captures and Opus-encodes the mic once and fans each frame out across
 * every target - a single camera's talk control is one target holding one id.
 */
export interface TalkTarget {
    serverId: string;
    deviceIds: readonly string[];
}

/** A live talk, as another talk control shows it: the loudness of the mic, and its end. */
export interface TalkSession {
    /** 0..1. */
    level: number;
    stop: () => void;
}
